import {FC, useState} from 'react'
import classNames from 'classnames'
import {FormattedMessage} from 'react-intl'
import styles from './EmployerContactRequests.module.scss'
import EmployerRequestsLayout from 'Layouts/EmployerRequestsLayout'
import {ContactRequestStatus} from 'Components/EmployerDashboard/InterviewInvitationListItem/Constants'
import {ContactRequestsDisplayType} from 'Components/EmployerDashboard/ContactRequestListItem/ContactRequestsListItem'
import {EmployerDashboardTranslations as edt} from 'Services/I18n/Constants'
import InfiniteScroll from 'Components/InfiniteScroll'
import ContactRequestsListItem from 'Components/EmployerDashboard/ContactRequestListItem'
import {useMatchingRoute} from 'Services/I18n/Hooks/useMatchingRoute'
import {
  EmployerRequestsSearchParamsType,
  EmployerRequestsStateType,
} from 'Interfaces'
import {ArrowDropDown} from '@mui/icons-material'
import Loader from 'Components/Fallbacks/Loader'
import {NoLongerActiveCandidateModal} from 'Components/Modal/NoLongerActiveCandidateModal'

const getRouteTab = (routeKey: string | undefined): string => {
  switch (routeKey) {
    case 'routes.employercontactrequestsPending':
      return 'pending'
    case 'routes.employercontactrequestsApproved':
      return 'approved'
    default:
      return 'all'
  }
}

type EmployerContactRequestsProps = {
  requests: EmployerRequestsStateType
  getSeekersFromContactRequests: (activeTab?: string) => void
  getEmployerContactRequests: (
    params?: EmployerRequestsSearchParamsType
  ) => void
  loadMore: (url: string) => void
  totalContactRequests?: number
}

export const EmployerContactRequests: FC<EmployerContactRequestsProps> = ({
  requests,
  getSeekersFromContactRequests,
  getEmployerContactRequests,
  loadMore,
  totalContactRequests,
}) => {
  const matchingRoute = useMatchingRoute()
  const routeTab = getRouteTab(matchingRoute.routeKey)
  const [order, setOrder] = useState('DESC')
  const [sort, setSort] = useState('status')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const onSort = (value: string) => {
    if (sort === value) {
      setOrder((old) => (old === 'ASC' ? 'DESC' : 'ASC'))
    } else {
      setSort(value)
      setOrder('ASC')
    }
    getEmployerContactRequests({...requests.params, sort, order})
  }

  let itemDisplayType: ContactRequestsDisplayType = 'all'
  if (routeTab === 'pending') itemDisplayType = 'pending'
  if (routeTab === 'approved') itemDisplayType = 'approved'

  const HeaderTitle = ({value, title}: {value: string; title: string}) => {
    const className = classNames(styles.sortBtn, {
      [styles.active]: value === sort,
      [styles.desc]: order === 'DESC',
    })

    return (
      <div onClick={() => onSort(value)} className={className}>
        <FormattedMessage id={title} />
        <ArrowDropDown />
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <EmployerRequestsLayout
        onSearch={(params) => {
          let statuses: string[] | undefined = []
          if (routeTab !== 'all' || params.statuses) {
            statuses = params.statuses || requests?.params?.statuses
          }
          getEmployerContactRequests({...requests.params, statuses, ...params})
        }}
        getSeekers={getSeekersFromContactRequests}
        tabs={['all', 'pending', 'approved']}
        activeTab={routeTab}
        type="requests"
        statusOptions={Object.values(ContactRequestStatus)}
        candidatesOptions={requests.candidatesOptions}
      >
        <div
          className={classNames(styles.contentHeader, {
            [styles.all]: itemDisplayType === 'all',
          })}
        >
          <HeaderTitle value="seekerName" title={edt.candidate} />
          <HeaderTitle
            value="requestType"
            title={edt.contactRequestRequestType}
          />
          {itemDisplayType !== 'pending' && (
            <HeaderTitle value="status" title={edt.status} />
          )}

          {itemDisplayType !== 'approved' && (
            <HeaderTitle value="expiration" title={edt.expire} />
          )}
          <div className={styles.leftMargin}>
            <FormattedMessage id={edt.action} />
          </div>
        </div>
        <InfiniteScroll
          loading={requests.loadMoreProgress}
          hasMore={
            !!(
              requests.total &&
              requests.loadMore &&
              totalContactRequests &&
              totalContactRequests > requests.searchResults.length
            )
          }
          loadMore={() => loadMore(requests.loadMore as string)}
        >
          <div className={styles.allContactRequestsItems}>
            {requests.loading ? (
              <div className={styles.loaderContainer}>
                <Loader />
                <span className={styles.loadingText}>
                  <FormattedMessage id={edt.loadingText} />
                </span>
              </div>
            ) : requests.error ? (
              <div>{requests.error.message || 'Error'}</div>
            ) : requests.searchResults.length > 0 ? (
              requests.searchResults.map((request, idx) => (
                <ContactRequestsListItem
                  request={request}
                  key={`contact-requests-${idx}`}
                  displayType={itemDisplayType}
                  setIsModalOpen={setIsModalOpen}
                />
              ))
            ) : (
              <div className={styles.noResults}>
                <FormattedMessage id={edt.noResults} />
              </div>
            )}
          </div>
        </InfiniteScroll>
      </EmployerRequestsLayout>
      <NoLongerActiveCandidateModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
      />
    </div>
  )
}
